import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NewsArticle } from '../types';
import './Media.css';

const Media: React.FC = () => {
  const { t, i18n } = useTranslation();
  const showAlbanianSubtitles = i18n.language?.startsWith('sq');
  const [activeTab, setActiveTab] = useState<'news' | 'gallery' | 'videos'>('news');
  const [selectedImage, setSelectedImage] = useState<{ url: string; caption: string } | null>(null);

  // Check for hash in URL to set active tab
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#gallery') {
      setActiveTab('gallery');
    } else if (hash === '#videos') {
      setActiveTab('videos');
    }
  }, []);

  const newsArticles: NewsArticle[] = [
    {
      id: 'summer-2026',
      title: t('news.articles.summer2026.title'),
      excerpt: t('news.articles.summer2026.excerpt'),
      content: t('news.articles.summer2026.content'),
      publishDate: '2026-09-06',
      author: t('news.authors.forum'),
      tags: [t('news.tags.internationalForum'), t('news.tags.peaceBuilding'), t('news.tags.ai')],
      image: '/events/summer-meeting-2026-invitation.jpg'
    },
    {
      id: '1',
      title: t('news.articles.secondEdition.title'),
      excerpt: t('news.articles.secondEdition.excerpt'),
      content: t('news.articles.secondEdition.content'),
      publishDate: '2025-11-05',
      author: t('news.authors.rotaryClub'),
      tags: [t('news.tags.edition2025'), t('news.tags.success'), t('news.tags.internationalForum'), t('news.tags.peaceBuilding')]
    },
    {
      id: 'first-edition-success',
      title: t('news.firstEdition.title'),
      excerpt: t('news.firstEdition.excerpt'),
      content: t('news.firstEdition.content'),
      publishDate: '2024-09-12',
      author: t('news.authors.forum'),
      tags: [t('news.tags.edition2024'), t('news.tags.success'), t('news.tags.internationalForum'), t('news.tags.peaceBuilding')],
      image: '/First edition announcement.png'
    },
    {
      id: '2',
      title: t('news.articles.success2024.title'),
      excerpt: t('news.articles.success2024.excerpt'),
      content: t('news.articles.success2024.excerpt'),
      publishDate: '2024-09-12',
      author: 'Telegrafi',
      tags: [t('news.tags.edition2024'), t('news.tags.success'), t('news.tags.peaceBuilding')],
      source: 'https://telegrafi.com/en/peace-has-no-alternatives%2C-the-first-edition-of-the-peace-forum-in-Peje-is-considered-successful/'
    },
    {
      id: '3',
      title: t('news.articles.ukraine.title'),
      excerpt: t('news.articles.ukraine.excerpt'),
      content: t('news.articles.ukraine.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      tags: [t('news.tags.ukraine'), t('news.tags.internationalSpeakers'), t('news.tags.resilience')],
      source: 'https://telegrafi.com/en/Stebljanko-at-the-peace-forum-in-Peje%2C-Ukraine-remains-strong-despite-the-economic-losses/'
    },
    {
      id: '4',
      title: t('news.articles.ai.title'),
      excerpt: t('news.articles.ai.excerpt'),
      content: t('news.articles.ai.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      tags: [t('news.tags.ai'), t('news.tags.media'), t('news.tags.journalism')],
      source: 'https://telegrafi.com/en/artificial-intelligence-and-media-influence-hajrullahu-journalism-has-a-decisive-role-in-educating-the-public-about-it/'
    },
    {
      id: '5',
      title: t('news.articles.university.title'),
      excerpt: t('news.articles.university.excerpt'),
      content: t('news.articles.university.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      tags: [t('news.tags.university'), t('news.tags.education'), t('news.tags.academicImpact')],
      source: 'https://telegrafi.com/en/the-rector-of-Haxhi-Zeka-Krasniqi-University%2C-the-conclusions-from-the-peace-forum-are-also-important-for-the-academic-field/'
    },
    {
      id: '6',
      title: t('news.articles.conflictPrevention.title'),
      excerpt: t('news.articles.conflictPrevention.excerpt'),
      content: t('news.articles.conflictPrevention.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      tags: [t('news.tags.conflictPrevention'), t('news.tags.peaceBuilding'), t('news.tags.communityEngagement')],
      source: 'https://telegrafi.com/en/peace-forum-in-Peje-Asllani%2C-our-role-is-to-prevent-conflicts-and-build-peace/'
    },
    {
      id: '7',
      title: t('news.articles.firstEdition.title'),
      excerpt: t('news.articles.firstEdition.excerpt'),
      content: t('news.articles.firstEdition.excerpt'),
      publishDate: '2024-09-11',
      author: 'Telegrafi',
      tags: [t('news.tags.firstEdition'), t('news.tags.internationalForum'), t('news.tags.peaceBuilding')],
      source: 'https://telegrafi.com/en/per-te-pare-ne-peje-mbahet-forumi-paqes-teme-ndertimi-paqes-ne-nje-bote-rivaliteti/'
    }
  ];

  // Removed press releases

  const galleryImages = [
    { id: 'invitation-2026', url: '/events/summer-meeting-2026-invitation.jpg', caption: t('events.summer2026.invitationCaption') },
    { id: 'agenda-2026', url: '/events/summer-meeting-2026-agenda.jpg', caption: t('events.summer2026.agendaCaption') },
    { id: '1', url: '/Opening speeches.jpg', caption: t('gallery.opening.title') },
    { id: '2', url: '/First Panel - Human Rights and Peace Education.jpg', caption: t('gallery.session1.title') },
    { id: '3', url: '/Second Panel - Artificial Intelligence, Media and Peace.jpg', caption: t('gallery.session2.title') },
    { id: '4', url: '/Third Panel - Peace and Economic Development.jpg', caption: t('gallery.session3.title') },
    { id: '5', url: '/Fourth Panel - Peacebuilding and Conflict Prevention.jpg', caption: t('gallery.session4.title') },
    { id: '6', url: '/First edition announcement.png', caption: t('gallery.announcement.title') },
    // Pictures from the First Edition
    { id: '7', url: '/First Edition photos/20240911_091117.jpg', caption: t('media.firstEditionPictures') },
    { id: '8', url: '/First Edition photos/20240911_091917.jpg', caption: t('media.firstEditionPictures') },
    { id: '9', url: '/First Edition photos/20240911_092626.jpg', caption: t('media.firstEditionPictures') },
    { id: '10', url: '/First Edition photos/20240911_093113.jpg', caption: t('media.firstEditionPictures') },
    { id: '11', url: '/First Edition photos/20240911_093152.jpg', caption: t('media.firstEditionPictures') },
    { id: '12', url: '/First Edition photos/20240911_093401.jpg', caption: t('media.firstEditionPictures') },
    { id: '13', url: '/First Edition photos/20240911_094917.jpg', caption: t('media.firstEditionPictures') },
    { id: '14', url: '/First Edition photos/20240911_094928.jpg', caption: t('media.firstEditionPictures') },
    { id: '15', url: '/First Edition photos/20240911_095920.jpg', caption: t('media.firstEditionPictures') },
    { id: '16', url: '/First Edition photos/20240911_100047.jpg', caption: t('media.firstEditionPictures') },
    { id: '17', url: '/First Edition photos/20240911_100050.jpg', caption: t('media.firstEditionPictures') },
    { id: '18', url: '/First Edition photos/20240911_100211.jpg', caption: t('media.firstEditionPictures') },
    { id: '19', url: '/First Edition photos/20240911_100217.jpg', caption: t('media.firstEditionPictures') },
    { id: '20', url: '/First Edition photos/20240911_100224.jpg', caption: t('media.firstEditionPictures') },
    { id: '21', url: '/First Edition photos/20240911_100227.jpg', caption: t('media.firstEditionPictures') },
    { id: '22', url: '/First Edition photos/20240911_100316.jpg', caption: t('media.firstEditionPictures') },
    { id: '23', url: '/First Edition photos/20240911_100328.jpg', caption: t('media.firstEditionPictures') },
    { id: '24', url: '/First Edition photos/20240911_104946.jpg', caption: t('media.firstEditionPictures') },
    { id: '25', url: '/First Edition photos/20240911_105007.jpg', caption: t('media.firstEditionPictures') },
    { id: '26', url: '/First Edition photos/20240911_105013.jpg', caption: t('media.firstEditionPictures') },
    { id: '27', url: '/First Edition photos/20240911_105630.jpg', caption: t('media.firstEditionPictures') },
    { id: '28', url: '/First Edition photos/20240911_105943.jpg', caption: t('media.firstEditionPictures') },
    { id: '29', url: '/First Edition photos/20240911_110039.jpg', caption: t('media.firstEditionPictures') },
    { id: '30', url: '/First Edition photos/20240911_112446.jpg', caption: t('media.firstEditionPictures') },
    { id: '31', url: '/First Edition photos/20240911_112611.jpg', caption: t('media.firstEditionPictures') },
    { id: '32', url: '/First Edition photos/20240911_112819.jpg', caption: t('media.firstEditionPictures') },
    { id: '33', url: '/First Edition photos/20240911_112824.jpg', caption: t('media.firstEditionPictures') },
    { id: '34', url: '/First Edition photos/20240911_112831.jpg', caption: t('media.firstEditionPictures') },
    { id: '35', url: '/First Edition photos/20240911_114214.jpg', caption: t('media.firstEditionPictures') },
    { id: '36', url: '/First Edition photos/20240911_120627.jpg', caption: t('media.firstEditionPictures') },
    { id: '37', url: '/First Edition photos/20240911_120948.jpg', caption: t('media.firstEditionPictures') },
    { id: '38', url: '/First Edition photos/20240911_123121.jpg', caption: t('media.firstEditionPictures') },
    { id: '39', url: '/First Edition photos/20240911_141331.jpg', caption: t('media.firstEditionPictures') },
    { id: '40', url: '/First Edition photos/20240911_141339.jpg', caption: t('media.firstEditionPictures') }
  ];

  return (
    <div className="media">
      <section className="media-hero section">
        <div className="container">
          <div className="media-hero-content text-center">
            <h1>{t('media.hero.title')}</h1>
            <p className="hero-subtitle">
              {t('media.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="media-navigation">
        <div className="container">
          <div className="media-tabs">
            <button 
              className={`tab-button ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => setActiveTab('news')}
            >
              {t('navigation.news')}
            </button>
            {/* Press releases tab removed */}
            <button 
              className={`tab-button ${activeTab === 'gallery' ? 'active' : ''}`}
              onClick={() => setActiveTab('gallery')}
            >
              {t('navigation.gallery')}
            </button>
            <button 
              className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
            >
              {t('media.tabs.videos')}
            </button>
          </div>
        </div>
      </section>

      <section className="media-content section">
        <div className="container">
          {activeTab === 'news' && (
            <div className="news-section">
              <h2 className="text-center">{t('news.title')}</h2>
              <div className="news-grid">
                {newsArticles.map((article) => (
                  <div key={article.id} className="news-card">
                    <h3 className="news-title">{article.title}</h3>
                    <p className="news-excerpt">{article.excerpt}</p>
                    {article.tags && article.tags.length > 0 && (
                      <div className="news-tags">
                        {article.tags.map((tag, index) => (
                          <span key={index} className="news-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="news-actions">
                      <a href={`/article/${article.id}`} className="news-link">
                        {t('common.readMore')}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Press releases content removed */}

          {activeTab === 'gallery' && (
            <div className="gallery-section">
              <h2 className="text-center">{t('media.tabs.gallery')}</h2>
              <div className="gallery-grid">
                {galleryImages.map((image) => (
                  <div key={image.id} className="gallery-item" onClick={() => setSelectedImage({ url: image.url, caption: image.caption })}>
                    <img 
                      src={image.url} 
                      alt={image.caption}
                      onError={(e) => {
                        e.currentTarget.src = '/images/placeholder-gallery.jpg';
                      }}
                    />
                    <div className="gallery-caption">{image.caption}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'videos' && (
            <div className="videos-section">
              <h2 className="text-center">{t('media.tabs.videos')}</h2>
              <div className="videos-grid">
                <div className="video-card">
                  <div className="video-thumbnail">
                    <video
                      key={`keynote-${showAlbanianSubtitles ? 'sq' : 'en'}`}
                      controls
                      className="video-player"
                      poster="/Peja Peace Forum 2nd Edition/General Wesley Clark.jpg"
                      crossOrigin="anonymous"
                    >
                      <source src="/videos/wesley-clark-keynote.mp4" type="video/mp4" />
                      {showAlbanianSubtitles && (
                        <track
                          kind="subtitles"
                          src="/videos/wesley-clark-keynote.sq.vtt"
                          srcLang="sq"
                          label="Shqip"
                          default
                        />
                      )}
                      {t('video.browserNotSupported')}
                    </video>
                  </div>
                  <div className="video-info">
                    <h3>{t('media.video.keynote.title')}</h3>
                    <p>{t('media.video.keynote.description')}</p>
                    <div className="video-meta">
                      <span className="video-date">{t('media.video.keynote.postedDate')}</span>
                      <span className="video-platform">{t('media.video.platform')}</span>
                    </div>
                  </div>
                </div>
                <div className="video-card">
                  <div className="video-thumbnail">
                    <video 
                      controls 
                      className="video-player"
                      poster="/images/video-poster.jpg"
                    >
                      <source src="/Video announcement.mp4" type="video/mp4" />
                      {t('video.browserNotSupported')}
                    </video>
                  </div>
                  <div className="video-info">
                    <h3>{t('media.video.title')}</h3>
                    <p>{t('media.video.description')}</p>
                    <div className="video-meta">
                      <span className="video-date">{t('media.video.postedDate')}</span>
                      <span className="video-platform">{t('media.video.platform')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={selectedImage.url} alt={selectedImage.caption} />
            <div className="modal-caption">{selectedImage.caption}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Media;
